<template>
  <div class="row no-wrap items-center q-gutter-x-xs">
    <q-input
      v-model="degrees"
      type="number"
      :min="0"
      :max="degreeLimit"
      dense
      outlined
      :disable="disable"
      :placeholder="$t('_RSFAP_SHARED_COORDINATES_SEPARATED_DEGREES')"
      class="col"
      @update:model-value="updateModel"
      @blur="clampDegrees"
    />
    <q-input
      v-model="minutes"
      type="number"
      :min="0"
      :max="59"
      dense
      outlined
      :disable="disable"
      :placeholder="$t('_RSFAP_SHARED_COORDINATES_SEPARATED_MINUTES')"
      class="col"
      @update:model-value="updateModel"
      @blur="clampMinutes"
    />
    <q-input
      v-model="seconds"
      type="number"
      :min="0"
      :max="59"
      step="0.01"
      dense
      outlined
      :disable="disable"
      :placeholder="$t('_RSFAP_SHARED_COORDINATES_SEPARATED_SECONDS')"
      class="col"
      @update:model-value="updateModel"
      @blur="clampSeconds"
    />
    <q-select
      v-model="direction"
      :options="directionOptions"
      dense
      outlined
      :disable="disable"
      style="min-width: 65px"
      @update:model-value="updateModel"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from "vue"

import * as coordinatesService from "@/services/coordinates.js"

const props = defineProps({
  modelValue: {
    type: Number,
    default: null
  },
  type: {
    type: String,
    required: true,
    validator: (value) => ["latitude", "longitude"].includes(value)
  },
  disable: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(["update:modelValue", "change"])

const degrees = ref(null)
const minutes = ref(null)
const seconds = ref(null)
const direction = ref(null)

const degreeLimit = computed(() => (props.type === "latitude" ? 90 : 180))
const directionOptions = computed(() => (props.type === "latitude" ? ["N", "S"] : ["E", "W"]))

const isInternalUpdate = ref(false)

const setValues = () => {
  if (props.modelValue != null) {
    const data = coordinatesService.decimalToDegreesAbsoluteValues(props.modelValue)
    degrees.value = data.degrees
    minutes.value = data.minutes
    seconds.value = Number(data.seconds)

    if (props.modelValue >= 0) {
      direction.value = props.type === "latitude" ? "N" : "E"
    } else {
      direction.value = props.type === "latitude" ? "S" : "W"
    }
  } else {
    degrees.value = null
    minutes.value = null
    seconds.value = null
    direction.value = directionOptions.value[0]
  }
}

const clamp = (value, min, max) => Math.min(Math.max(min, Number(value) || 0), max)

const clampDegrees = () => {
  degrees.value = clamp(degrees.value, 0, degreeLimit.value)
  updateModel()
}

const clampMinutes = () => {
  minutes.value = clamp(minutes.value, 0, 59)
  updateModel()
}

const clampSeconds = () => {
  seconds.value = clamp(seconds.value, 0, 59)
  updateModel()
}

const updateModel = () => {
  const d = Number(degrees.value) || 0
  const m = Number(minutes.value) || 0
  const s = Number(seconds.value) || 0
  const dir = direction.value === "N" || direction.value === "E" ? 1 : -1
  const value = coordinatesService.degreesToDecimal(d, m, s, dir)

  isInternalUpdate.value = true
  emit("update:modelValue", value || null)
  emit("change")
  nextTick(() => {
    isInternalUpdate.value = false
  })
}

watch(
  () => props.modelValue,
  () => {
    if (!isInternalUpdate.value) {
      setValues()
    }
  },
  { immediate: true }
)
</script>
